<template>
  <div v-if="isTaxInfoNeeded">
    <zm-form-item ref="vatNumberRef" prop="vatNumber" :rules="vatNumberRules" :label="taxPlaceholder" size="xLarge" for="tax-certificate-id" class="tax-exempt-form" v-loading="verifying" v-if="taxType === TAX_TYPE.INPUT">
      <div class="tax-exempt-form__content">
        <div v-if="taxMessage" class="tax-exempt-form__message" v-html='taxMessage'></div>
        <span :class="['tax-exempt-form__tax-input', {'disabled': isTaxNumberDisabled}]">
          <div v-if="taxPrefix4Template" class="tax-exempt-form__prefix">{{ taxPrefix4Template }}</div>
          <zm-input maxlength="20" :disabled="isTaxNumberDisabled" :class="['tax-exempt-form__id', {'with-prefix': taxPrefix4Template}, {'with-suffix': taxSuffix}]" id="tax-certificate-id" v-model="form.vatNumber" @change="emitVatNumber"></zm-input>
          <div v-if="taxSuffix" class="tax-exempt-form__suffix">{{ taxSuffix }}</div>

          <span v-if="!isTaxNumberDisabled" :class="['tax-exempt-form__mark', {'with-suffix': taxSuffix}]">
            <i v-if="taxStatus === TAX_ID_STATUS.VERIFIED" class="zm-icon-success main-icon"/>
            <i v-if="taxStatus === TAX_ID_STATUS.FAILED" class="zm-icon-error error-icon"/>
          </span>

          <template v-if="checkboxDesc">
            <zm-checkbox class="tax-exempt-form__exempt-status" v-model="form.notVatRegistered" name="taxExempt" @change="emitTaxExemptStatus">{{ checkboxDesc }}</zm-checkbox>
            <zm-tooltip :content="dialogContent" placement="top" role="button"
            :aria-label="'learn more about '+ checkboxDesc">
              <i class="zm-icon-info-outline main-icon"></i>
            </zm-tooltip>
          </template>
        </span>
      </div>
    </zm-form-item>

    <div class="tax-exempt-form-dropdown" v-if="taxType === TAX_TYPE.DROPDOWN">
      <div v-if="taxMessage" class="tax-exempt-form-dropdown__message" v-html='taxMessage'></div>

      <zm-select v-model="form.vatNumber" @change="toggleVatRegistered">
        <zm-option value="" label="---" />
        <zm-option value="VAT Agent" />
        <zm-option value="Agente de IVA" />
      </zm-select>
    </div>

    <zm-form-item v-if="showTaxRequirement" class="tax-exempt-form__tax-requirement" prop="agreeTaxRequirement" :rules="taxRequirementRules">
      <zm-checkbox class="tax-exempt-form__tax-requirement__checkbox" v-model="agreeTaxRequirement">
        <span v-html="__$t('billing.vat.tax_requirement.desc', { email: whtCertificateEmail })"></span>
      </zm-checkbox>
    </zm-form-item>
  </div>
</template>

<script>
import { assign } from 'lodash';
import { checkTaxNumber } from '../../apis/address';
import { TAX_ID_STATUS, TAX_TYPE, TAX_PREFIX, taxConfigurationGetter, EU_COUNTRIES, ONLINE_VALIDATION_COUNTRIES, COUNTRY_REPLACEMENT, NO_PREFIX_COUNTRY } from '../../utils/address';
import {translateMixins, translate} from '../../plugins/i18n';
import {APP_NAMESPACE} from '../../store';
import trackingMixin from "../mixins/trackingMixin";
import {mapGetters, mapMutations} from "vuex";
import { cachePromise } from '../../common/cache'

export default {
  name: 'tax-form',
  mixins: [translateMixins, trackingMixin],
  data() {
    const validateSaudiTaxRequirement = (rule, value, callback) => {
      if (this.agreeTaxRequirement) {
        callback()
      } else {
        callback(new Error(this.__$t('billing.vat.tax_requirement.invalid_msg')))
      }
    }
    const vatNumberValidator = (rule, value, callback) => {
      if (this.form.notVatRegistered && !this.currentTaxInfo.revert) {
        callback();
      } else {
        if (value.trim() === '') {
          if (this.currentTaxInfo.isEmptiable && this.form.notVatRegistered === false) {
            callback();
          }
          callback(new Error('Tax number is required'));
        } else if (this.taxStatus === TAX_ID_STATUS.FAILED) {
          callback(new Error('This tax number is not valid'));
        } else {
          callback();
        }
      }
    };
    return {
      vatNumberRules: [{ validator: vatNumberValidator, trigger: ["blur","change"] }],
      showVatDialog: false,
      i18nData: {
        dialogTitle: this.__$t('billing.not_vat_registered'),
        dialogContent: this.__$t('billing.vat_checkbox_desc'),
      },
      status: '',
      verifying: false,
      TAX_ID_STATUS: TAX_ID_STATUS,
      taxRequirementRules: [{ validator: validateSaudiTaxRequirement, trigger: 'change' }],
      agreeTaxRequirement: false,
      whtCertificateEmail: 'whtcertificate@zoom.us',
    }
  },
  props: {
    form: {
      type: Object,
    },
    taxStatus: {
      type: String,
    },
    country: {
      type: String,
    },
    enableTaxRequirement: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters(APP_NAMESPACE, ['taxInfo']),
    TAX_TYPE() {
      return TAX_TYPE;
    },
    isTaxInfoNeeded() {
      return EU_COUNTRIES.indexOf(this.form.country) !== -1 || this.taxConfiguration[this.form.country];
    },
    taxMessage() {
      if (this.taxConfiguration[this.country]?.message !== undefined) {
        return this.taxConfiguration[this.country]?.message;
      } else {
        return this.__$t('billing.not_vat_tips');
      }
    },
    taxType() {
      if (this.currentTaxInfo.type === TAX_TYPE.DROPDOWN) {
        return TAX_TYPE.DROPDOWN;
      }

      return TAX_TYPE.INPUT;
    },
    taxPrefix() {
      return this.getTaxPrefix()
    },
    taxPrefix4Template() {
      return this.getTaxPrefix(true)
    },
    taxSuffix() {
      return this.taxConfiguration[this.country]?.suffix;
    },
    taxPlaceholder() {
      return this.taxConfiguration[this.country]?.placeholder || this.__$t('billing.vat_number');
    },
    checkboxDesc() {
      if (this.isEUCountry) {
        return this.__$t('billing.not_vat_registered');
      }

      return this.taxConfiguration[this.country]?.checkbox;
    },
    dialogContent() {
      return this.taxConfiguration[this.country]?.tooltip || this.i18nData.dialogContent;
    },
    isEUCountry() {
      return EU_COUNTRIES.indexOf(this.country) !== -1;
    },
    needOnlineValidation() {
      return this.isEUCountry || ONLINE_VALIDATION_COUNTRIES.indexOf(this.country) !== -1;
    },
    currentTaxInfo() {
      return this.taxConfiguration[this.country] || {};
    },
    isTaxNumberDisabled() {
      // from alan: Checkbox为Tax Exempt的时候，无论勾不勾选都不会阻拦用户去填写Tax Number; Checkbox为Not VAT Registered的时候，表示的是不勾选才免税，加了Revert这个条件以后表示勾选才免税，一般配合Checkbox为Tax Exempt的场景使用
      return !this.currentTaxInfo.revert && this.form.notVatRegistered;
    },
    showTaxRequirement({ country }) {
      return country === 'SA' && this.enableTaxRequirement
    },
    taxConfiguration() {
      return taxConfigurationGetter(translate)
    },
  },
  watch:{
    taxStatus(v) {
      if (v === TAX_ID_STATUS.VERIFIED) {
        this.$refs.vatNumberRef?.clearValidate()
      }
    },
    'form.country': {
      immediate: true,
      handler(v) {
        if (v) {
          this.initData()
          this.$refs.vatNumberRef?.clearValidate()
        }
      }
    }
  },
  mounted() {
    this.checkTaxNumber$Cache = cachePromise(checkTaxNumber);
  },
  methods: {
    ...mapMutations(APP_NAMESPACE, ['setTaxInfo']),
    getTaxPrefix(used4Template = false) {
      if (TAX_PREFIX[this.country]) {
        return TAX_PREFIX[this.country];
      } else if (this.taxConfiguration[this.country]?.prefix) {
        return used4Template ? this.taxConfiguration[this.country]?.prefix : this.country
      } else if (this.isEUCountry) {
        return this.country;
      } else {
        return '';
      }
    },
    async emitVatNumber() {
      const _this = this;
      this.verifying = true;
      let clientSideValidationResult;
      const taxConfigItem = this.taxConfiguration[this.country];

      if (this.isEUCountry) {
        clientSideValidationResult = window.checkVATNumber(this.taxPrefix + this.form.vatNumber);
      } else if (typeof taxConfigItem?.validation === 'object') {
        let trimmedVatNumber = this.form.vatNumber;

        if (taxConfigItem?.ignore) {
          trimmedVatNumber = this.form.vatNumber.replace(taxConfigItem?.ignore, '');
        }

        clientSideValidationResult = taxConfigItem.validation.test(trimmedVatNumber);

        if (this.currentTaxInfo.isEmptiable && this.form.vatNumber.trim().length === 0) {
          clientSideValidationResult = true;
        }
      } else if (typeof taxConfigItem?.validation === 'function') {
        clientSideValidationResult = taxConfigItem.validation(this.form.vatNumber);
        if (taxConfigItem?.isEmptiable === true && this.form.vatNumber.trim().length === 0) {
          clientSideValidationResult = true;
        }
      } else if (taxConfigItem?.isEmptiable !== true && this.form.vatNumber.trim().length === 0) {
        clientSideValidationResult = false
      } else {
        clientSideValidationResult = true;
      }

      if (clientSideValidationResult) {
        if (this.needOnlineValidation && this.form.vatNumber.trim().length !== 0) {
          const formData = new FormData();
          formData.append('countryCode', COUNTRY_REPLACEMENT[this.form.country] || this.form.country);
          formData.append('taxId', this.form.vatNumber);
          const response = await this.checkTaxNumber$Cache(formData);
          // checkTaxNumber(formData).then(response => {
            _this.verifying = false;
            const data = response.data;

            const taxDataValidation = taxConfigItem?.taxDataValidation || (() => true);
            if (response.result && data.format_valid && data.valid && taxDataValidation(data)) {
              _this.$emit('tax-status-change', TAX_ID_STATUS.VERIFIED);
               _this.setTaxInfo(assign({}, _this.taxInfo, {vatNumber: (NO_PREFIX_COUNTRY.indexOf(_this.country) === -1 ? _this.taxPrefix : '') + _this.form.vatNumber}));

              if (typeof this.currentTaxInfo?.taxExempt === 'function') {
                const isTaxExempt = this.currentTaxInfo.taxExempt(data);
                this.setTaxInfo(assign({}, this.taxInfo, { notVatRegistered: !isTaxExempt }));
              } else if (this.currentTaxInfo?.taxExempt === true) {
                this.setTaxInfo(assign({}, this.taxInfo, { notVatRegistered: false }));
              }
            } else {
              _this.$emit('tax-status-change', TAX_ID_STATUS.FAILED)
            }
          // });
        } else {
          this.verifying = false;

          if (this.form.vatNumber.trim().length === 0) {
            this.$emit('tax-status-change', '');
          } else {
            this.$emit('tax-status-change', TAX_ID_STATUS.VERIFIED);
          }
          this.setTaxInfo(assign({}, _this.taxInfo, {vatNumber: (NO_PREFIX_COUNTRY.indexOf(_this.country) === -1 ? _this.taxPrefix : '') + _this.form.vatNumber}));

          if (this.currentTaxInfo?.taxExempt) {
            this.setTaxInfo(assign({}, this.taxInfo, {notVatRegistered: false}));
          } else if (this.currentTaxInfo?.checkbox === undefined) {
            this.setTaxInfo(assign({}, this.taxInfo, {notVatRegistered: true}));
          }
        }
      } else {
        this.$emit('tax-status-change', TAX_ID_STATUS.FAILED);
        this.verifying = false;
      }
    },
    async emitTaxExemptStatus() {
      if (this.form.notVatRegistered) {
        await this.$confirm(
            this.dialogContent,
            this.checkboxDesc,
            {
              confirmButtonText: 'Yes',
              cancelButtonText: 'No',
            }
        ).then(() => {
          if (this.currentTaxInfo.revert) {
            return;
          }

          this.form.vatNumber = '';
          this.$emit('tax-status-change', '');
          this.setTaxInfo(assign({}, this.taxInfo, {vatNumber: ''}));
        }).catch(() => {
          this.form.notVatRegistered = false;
        });
      }

      let notVatRegistered = this.form.notVatRegistered;

      if (this.currentTaxInfo?.revert) {
        notVatRegistered = !notVatRegistered;
      }
      this.setTaxInfo(assign({}, this.taxInfo, {notVatRegistered: notVatRegistered}));
      this.emitTracking('setTaxInfo', this.checkboxDesc, notVatRegistered)
    },
    initTaxExemptStatus() {
      let notVatRegistered = this.form.notVatRegistered;

      if (this.currentTaxInfo?.revert) {
        notVatRegistered = !notVatRegistered;
      }
      this.setTaxInfo(assign({}, this.taxInfo, {notVatRegistered: notVatRegistered}));
    },
    toggleVatRegistered() {
      const vatNumber = this.form.vatNumber;
      this.setTaxInfo(assign({}, this.taxInfo, {vatNumber: vatNumber}));

      if (vatNumber === '') {
        this.form.notVatRegistered = true;
        this.setTaxInfo(assign({}, this.taxInfo, {notVatRegistered: true}));
      } else {
        this.form.notVatRegistered = false;
        this.setTaxInfo(assign({}, this.taxInfo, {notVatRegistered: false}));
      }
    },
    initData() {
      // Initialize the tax info according to the billing info, need to do some trim for the VAT Number due to the prefix
      let vatNumber = this.taxInfo.vatNumber;
      if (vatNumber) {
        if (typeof vatNumber === 'string') {
          if (vatNumber.startsWith(this.taxPrefix)) {
            vatNumber = vatNumber.slice(this.taxPrefix.length);
          }
          if (vatNumber.endsWith(this.taxSuffix)) {
            vatNumber = vatNumber.slice(0, -this.taxSuffix.length);
          }
          this.form.vatNumber = vatNumber;
        }
      }
      this.initTaxExemptStatus();
      if (this.form.vatNumber) {
        this.emitVatNumber();
      }
    }
  },
};
</script>

<style lang="scss">
  @import '../../styles/_variables.scss';
  @import '@zoom/billing-responsive/styles/responsive.scss';


  $bg-disabled: #fff;

  .tax-exempt-form {
    margin-top: 24px;

    &__message {
      margin-bottom: 12px;
    }

    &__prefix {
      width: 10%;
      border: 1px solid #b8b8c4;
      border-right: 0;
      border-radius: 4px 0 0 4px;
      text-align: center;
      line-height: 46px;
      background: $bg-disabled;
      color: #747478;
    }

    &__suffix {
      width: 10%;
      border: 1px solid #b8b8c4;
      border-left: 0;
      border-radius: 0 4px 4px 0;
      text-align: center;
      line-height: 46px;
      background: $bg-disabled;
      color: #747478;
    }

    &__tax-input {
      position: relative;
      display: flex;
      align-items: center;
      .zm-input.is-disabled .zm-input__inner{
        background: $bg-disabled;
      }

      &.disabled {
        .zm-input__label {
          cursor: not-allowed;
        }
      }
    }

    &__id {
      .zm-input__inner {
        border-radius: 4px;
      }

      width: 55%;

      &.with-prefix {
        .zm-input__inner {
          border-radius: 0 4px 4px 0;

          &:disabled {
            background: $bg-disabled;
          }
        }
      }

      &.with-suffix {
        .zm-input__inner {
          border-radius: 4px 0 0 4px;

          &:disabled {
            background: $bg-disabled;
          }
        }
      }
    }

    &__exempt-status {
      margin-left: 8px;
      margin-right: 4px;
    }

    &__content {
      position: relative;
    }

    &__mark {
      position: relative;
      right: 36px;

      &.with-suffix {
        right: 100px;
      }
    }

    @include media(mobile) {
      &__mark {
        position: relative;
        right: 14px;
        &.with-suffix {
          right: 50px;
        }
      }
    }

    .main-icon {
      color: $theme-color;
    }

    .error-icon {
      color: $error-color;
    }

    &__tax-requirement {
      margin-top: 12px;
      &__checkbox {
        margin-right: 0;
      }
    }
  }

  .tax-exempt-form-dropdown {
    margin-top: 18px;

    .zm-select-span__inner:not(.is-placeholder) {
      margin: 8px 0 0 12px;
    }

    &__message {
      margin-bottom: 4px;
    }
  }
</style>
